<script>
    import { onMount } from "svelte";
    import { push } from "svelte-spa-router";
    import { db } from "../db/index.js";
    import { RESOURCE_TYPES } from "@edu-quizz/shared/constants/resourceTypes";

    export let params = {};

    let article = null;
    let attachedResources = [];
    let loading = true;
    let error = null;

    async function loadArticle() {
        loading = true;
        error = null;

        try {
            const articleId = parseInt(params.id);

            // Get the article with its attached resources from the database
            const articleData =
                await db.getArticleWithAttachedResources(articleId);

            if (!articleData) {
                error = "Article non trouvé";
                loading = false;
                return;
            }

            article = articleData;
            attachedResources = articleData.attachedResources || [];

            loading = false;
        } catch (err) {
            console.error("Error loading article:", err);
            error = err.message;
            loading = false;
        }
    }

    function goBack() {
        push("/");
    }

    function navigateToResource(resource) {
        if (resource.type === RESOURCE_TYPES.QUIZ) {
            push(`/quiz/${resource.serverResourceId}`);
        } else if (resource.type === RESOURCE_TYPES.FLASHCARD_DECK) {
            push(`/flashcard/${resource.serverResourceId}`);
        } else if (resource.type === RESOURCE_TYPES.BOOKMARK && resource.url) {
            window.open(resource.url, "_blank");
        } else if (resource.type === RESOURCE_TYPES.ARTICLE) {
            push(`/article/${resource.serverResourceId}`);
        }
    }

    onMount(() => {
        loadArticle();
    });
</script>

<div class="article-page">
    <header class="page-header">
        <button class="btn btn-outline btn-content back-btn" on:click={goBack}>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
            >
                <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Retour
        </button>
        {#if article}
            <h1 class="text-content">{article.name}</h1>
        {/if}
    </header>

    {#if loading}
        <div class="loading">
            <div class="loading loading-spinner loading-lg text-primary spinner"></div>
            <p>Chargement de l'article...</p>
        </div>
    {:else if error}
        <div class="error">
            <div class="alert alert-error">
                <p>Erreur: {error}</p>
            </div>
            <button class="btn btn-primary" on:click={loadArticle}>Réessayer</button>
        </div>
    {:else if article}
        <div class="article-container">
            {#if article.description}
                <div class="alert border-l-4 border-l-primary article-meta">
                    <p class="article-description">{article.description}</p>
                    {#if article.level || article.subject}
                        <div class="article-tags">
                            {#if article.level}
                                <span class="badge badge-success badge-outline tag">{article.level}</span>
                            {/if}
                            {#if article.subject}
                                <span class="badge badge-primary badge-outline tag"
                                    >{article.subject}</span
                                >
                            {/if}
                        </div>
                    {/if}
                </div>
            {/if}

            <div class="card border-2 border-primary bg-base-100 shadow-md article-content prose prose-sm md:prose-base max-w-none text-left">
                {@html article.content}
            </div>

            {#if attachedResources.length > 0}
                <div class="attached-resources">
                    <h2 class="text-content">Ressources associées</h2>
                    <div class="resources-grid">
                        {#each attachedResources as resource}
                            <div
                                class="card bg-base-100 border-2 border-primary/20 hover:border-primary hover:shadow-lg resource-card"
                                on:click={() => navigateToResource(resource)}
                            >
                                <div class="badge badge-primary resource-type-badge">
                                    {#if resource.type === RESOURCE_TYPES.QUIZ}
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        >
                                            <path d="M9 11l3 3L22 4" />
                                            <path
                                                d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
                                            />
                                        </svg>
                                        Quiz
                                    {:else if resource.type === RESOURCE_TYPES.FLASHCARD_DECK}
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        >
                                            <rect
                                                x="2"
                                                y="7"
                                                width="20"
                                                height="15"
                                                rx="2"
                                                ry="2"
                                            />
                                            <polyline points="17 2 12 7 7 2" />
                                        </svg>
                                        Flashcards
                                    {:else if resource.type === RESOURCE_TYPES.BOOKMARK}
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        >
                                            <path
                                                d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
                                            />
                                            <path
                                                d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
                                            />
                                        </svg>
                                        Lien
                                    {:else if resource.type === RESOURCE_TYPES.ARTICLE}
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        >
                                            <path
                                                d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                                            />
                                            <polyline points="14 2 14 8 20 8" />
                                            <line
                                                x1="16"
                                                y1="13"
                                                x2="8"
                                                y2="13"
                                            />
                                            <line
                                                x1="16"
                                                y1="17"
                                                x2="8"
                                                y2="17"
                                            />
                                            <polyline points="10 9 9 9 8 9" />
                                        </svg>
                                        Article
                                    {/if}
                                </div>
                                <h3 class="text-base-content resource-title">
                                    {resource.name ||
                                        `Resource #${resource.serverId}`}
                                </h3>
                                {#if resource.description}
                                    <p class="text-base-content/70 resource-description">
                                        {resource.description}
                                    </p>
                                {/if}
                            </div>
                        {/each}
                    </div>
                </div>
            {/if}
        </div>
    {/if}
</div>

<style>
    .article-page {
        min-height: 100vh;
        padding: 1.5rem;
        max-width: 900px;
        margin: 0 auto;
    }

    .page-header {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        margin-bottom: 2rem;
    }

    .back-btn {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 1rem;
        font-size: 0.95rem;
        width: fit-content;
    }

    h1 {
        margin: 0;
        font-size: 2rem;
    }

    .loading,
    .error {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 60vh;
        gap: 1rem;
    }

    .spinner {
        width: 48px;
        height: 48px;
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }

    .error button {
        padding: 0.6rem 1.2rem;
        font-size: 0.95rem;
    }

    .article-container {
        display: flex;
        flex-direction: column;
        gap: 2rem;
    }

    .article-meta {
        padding: 1.5rem;
        border-radius: 8px;
    }

    .article-description {
        font-size: 1rem;
        line-height: 1.6;
        margin: 0 0 1rem 0;
    }

    .article-tags {
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
    }

    .tag {
        display: inline-block;
        padding: 0.4rem 0.8rem;
        font-size: 0.85rem;
        font-weight: 600;
    }

    .article-content {
        padding: 2rem;
        border-radius: 12px;
        text-align: left;
    }

    .attached-resources {
        margin-top: 2rem;
    }

    .attached-resources h2 {
        font-size: 1.5rem;
        margin-bottom: 1.5rem;
    }

    .resources-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 1.5rem;
    }

    .resource-card {
        border-radius: 12px;
        padding: 1.5rem;
        cursor: pointer;
        transition: all 0.2s;
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
    }

    .resource-card:hover {
        transform: translateY(-2px);
    }

    .resource-type-badge {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.4rem 0.8rem;
        border-radius: 6px;
        font-size: 0.85rem;
        font-weight: 600;
        text-transform: uppercase;
        width: fit-content;
    }

    .resource-title {
        font-size: 1.1rem;
        font-weight: 600;
        margin: 0;
    }

    .resource-description {
        font-size: 0.9rem;
        line-height: 1.5;
        margin: 0;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    @media (max-width: 640px) {
        .article-page {
            padding: 1rem;
        }

        h1 {
            font-size: 1.5rem;
        }

        .article-content {
            padding: 1.5rem;
        }

        .resources-grid {
            grid-template-columns: 1fr;
        }
    }
</style>
